import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  WebSocketStatus,
  ExtractionResult,
  FQPResponse,
  FQQResponse,
  PricingProgress,
} from '@/types/pricing';
import { fqpPricingAPI } from '@/utils/api/lawyerAPI';
import { BOOKING_EXTRACTION, WEBSOCKET_RECONNECT_MAX } from '@/config/timeouts';

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  onProgress?: (progress: PricingProgress) => void;
}

interface UsePricingWebSocketReturn {
  status: WebSocketStatus;
  sendExtractionRequest: (
    file: File,
    onProgress?: (attempt: number, maxAttempts: number) => void
  ) => Promise<ExtractionResult>;
  sendFQPCommand: (command: string) => Promise<FQPResponse>;
  sendFQQCommand: (fareNumber: string) => Promise<FQQResponse>;
  disconnect: () => void;
}

function generateMessageId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function usePricingWebSocket(): UsePricingWebSocketReturn {
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const pendingRequestsRef = useRef<Map<string, PendingRequest>>(new Map());
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setStatus('connecting');

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = import.meta.env.VITE_WS_HOST || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('connected');
      reconnectAttemptsRef.current = 0;
    };

    ws.onclose = () => {
      setStatus('disconnected');
      wsRef.current = null;

      // Reject all pending requests
      pendingRequestsRef.current.forEach((request) => {
        request.reject(new Error('WebSocket connection closed'));
      });
      pendingRequestsRef.current.clear();

      // Auto-reconnect with exponential backoff
      const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), WEBSOCKET_RECONNECT_MAX);
      reconnectAttemptsRef.current++;

      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    ws.onerror = () => {
      setStatus('error');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const messageId = data.message_id;

        if (!messageId) return;

        const pendingRequest = pendingRequestsRef.current.get(messageId);
        if (!pendingRequest) return;

        // Handle progress updates
        if (data.type === 'progress') {
          pendingRequest.onProgress?.({
            step: data.step_id,
            description: data.description,
            percentage: data.percentage,
          });
          return;
        }

        // Handle extraction progress (retry notifications)
        if (data.type === 'extraction_progress' || data.type === 'extraction_retry') {
          // These are informational, don't resolve yet
          return;
        }

        // Handle extraction complete
        if (data.type === 'extraction_complete') {
          pendingRequestsRef.current.delete(messageId);
          pendingRequest.resolve(data.data);
          return;
        }

        // Handle extraction error
        if (data.type === 'extraction_error') {
          pendingRequestsRef.current.delete(messageId);
          pendingRequest.reject(new Error(data.message || 'Extraction failed'));
          return;
        }

        // Handle FQP/FQQ response
        if (data.amadeus_response) {
          pendingRequestsRef.current.delete(messageId);
          pendingRequest.resolve(data.amadeus_response);
          return;
        }

        // Handle generic error
        if (data.error) {
          pendingRequestsRef.current.delete(messageId);
          pendingRequest.reject(new Error(data.error));
          return;
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const sendExtractionRequest = useCallback(
    async (
      file: File,
      onProgress?: (attempt: number, maxAttempts: number) => void
    ): Promise<ExtractionResult> => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket not connected');
      }

      const messageId = generateMessageId();
      const base64Data = await fileToBase64(file);

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          pendingRequestsRef.current.delete(messageId);
          reject(new Error(`Extraction request timed out after ${BOOKING_EXTRACTION / 1000} seconds`));
        }, BOOKING_EXTRACTION);

        pendingRequestsRef.current.set(messageId, {
          resolve: (value) => {
            clearTimeout(timeoutId);
            resolve(value as ExtractionResult);
          },
          reject: (reason) => {
            clearTimeout(timeoutId);
            reject(reason);
          },
          onProgress: (progress) => {
            // Extract attempt info from progress if available
            if (progress.description?.includes('Attempt')) {
              const match = progress.description.match(/Attempt (\d+)/);
              if (match && onProgress) {
                onProgress(parseInt(match[1], 10), 3);
              }
            }
          },
        });

        wsRef.current!.send(
          JSON.stringify({
            file: {
              name: file.name,
              type: file.type,
              data: base64Data,
            },
            message_id: messageId,
            extract_booking_confirmation: true,
          })
        );
      });
    },
    []
  );

  /**
   * Send FQP command via REST API (uses warm Amadeus session maintained by Celery)
   * This is fast because it uses the pre-authenticated session instead of creating a new one.
   */
  const sendFQPCommand = useCallback(
    async (command: string): Promise<FQPResponse> => {
      try {
        // Use REST API which uses the warm Amadeus session
        const result = await fqpPricingAPI.execute(command, false);

        if (result.error) {
          throw new Error(result.error);
        }

        // Convert REST API response to expected FQPResponse format
        return {
          meta: result.meta,
          fares: result.fares,
          raw_response: result.raw_response,
        };
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('FQP request failed');
      }
    },
    []
  );

  /**
   * Send FQQ command via REST API for fare details
   */
  const sendFQQCommand = useCallback(async (fareNumber: string): Promise<FQQResponse> => {
    try {
      // Convert to integer to remove leading zeros (e.g., "01" -> "1")
      const fareNumberInt = parseInt(fareNumber, 10);
      const fqqCommand = `FQQ${fareNumberInt}`;

      // Use REST API which uses the warm Amadeus session
      const result = await fqpPricingAPI.execute(fqqCommand, false);

      if (result.error) {
        throw new Error(result.error);
      }

      // Convert REST API response to expected FQQResponse format
      return {
        meta: result.meta,
        fares: result.fares,
        raw_response: result.raw_response,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('FQQ request failed');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  return {
    status,
    sendExtractionRequest,
    sendFQPCommand,
    sendFQQCommand,
    disconnect,
  };
}
